import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, Save, Pencil, Copy, Check, Mail, MessageSquare, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { TimeSelect, parseTime, composeTime } from "@/components/TimeSelect";

const input = "w-full rounded-sm bg-navy border border-gold/20 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold transition-shadow duration-200";
const label = "block text-xs uppercase tracking-wider text-slate-400 mb-1.5";
const STATUS = ["pending", "confirmed", "completed", "cancelled"];

export function EditBookingDialog({ booking, onSaved }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [links, setLinks] = useState(null);
  const [copied, setCopied] = useState("");
  const t0 = parseTime(booking.time);
  const [form, setForm] = useState({
    name: booking.name || "", phone: booking.phone || "", email: booking.email || "",
    pickup: booking.pickup || "", dropoff: booking.dropoff || "", date: booking.date || "",
    hour: t0.hour, minute: t0.minute, ampm: t0.ampm,
    amount: booking.amount != null ? String(booking.amount) : "",
    payment_method: booking.payment_method || "pay_later", status: booking.status || "pending",
    notes: booking.notes || "",
    driver_name: booking.driver_name || "", vehicle: booking.vehicle || "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const copy = (t) => { navigator.clipboard.writeText(t); setCopied(t); toast.success("Copied!"); setTimeout(() => setCopied(""), 1500); };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        pickup: form.pickup.trim() || null,
        dropoff: form.dropoff.trim() || null,
        date: form.date || null,
        time: composeTime(form.hour, form.minute, form.ampm) || null,
        payment_method: form.payment_method,
        status: form.status,
        notes: form.notes.trim() || null,
        driver_name: form.driver_name.trim() || null,
        vehicle: form.vehicle.trim() || null,
      };
      if (form.amount !== "" && !isNaN(parseFloat(form.amount))) payload.amount = parseFloat(form.amount);
      const { data } = await api.put(`/admin/bookings/${booking.id}`, payload);
      toast.success("Booking updated.");
      onSaved?.(data.booking);
      setLinks({ pay_url: data.pay_url, invoice_url: data.invoice_url });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update booking.");
    } finally {
      setLoading(false);
    }
  };

  const payMsg = links
    ? `Hi${form.name ? " " + form.name : ""}, here is your secure payment link for your agreed fare of $${form.amount || ""}: ${links.pay_url}`
    : "";
  const mailto = `mailto:${form.email || ""}?subject=${encodeURIComponent("Your payment link — Bridge Taxi")}&body=${encodeURIComponent(payMsg)}`;
  const sms = `sms:${form.phone || ""}?&body=${encodeURIComponent(payMsg)}`;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setLinks(null); }}>
      <button onClick={() => setOpen(true)} data-testid={`edit-booking-${booking.id}`}
        className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-sky-300 hover:text-sky-200">
        <Pencil className="h-3 w-3" /> Edit
      </button>
      <DialogContent className="bg-navy-light border border-gold/30 text-white max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-heading">Edit booking · {booking.id.slice(0, 8).toUpperCase()}</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">Set the agreed fare and any detail below — all fields are optional. Saving gives you a payment link to send.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Name</label><input data-testid="edit-name" value={form.name} onChange={set("name")} placeholder="Customer" className={input} /></div>
            <div><label className={label}>Phone</label><input data-testid="edit-phone" value={form.phone} onChange={set("phone")} className={input} /></div>
          </div>
          <div><label className={label}>Email</label><input data-testid="edit-email" value={form.email} onChange={set("email")} className={input} /></div>
          <div><label className={label}>Pickup location</label><input data-testid="edit-pickup" value={form.pickup} onChange={set("pickup")} className={input} /></div>
          <div><label className={label}>Drop-off location</label><input data-testid="edit-dropoff" value={form.dropoff} onChange={set("dropoff")} className={input} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Pickup date</label><input type="date" data-testid="edit-date" value={form.date} onChange={set("date")} className={input} /></div>
            <div><label className={label}>Pickup time</label>
              <TimeSelect dark idPrefix="edit-time" hour={form.hour} minute={form.minute} ampm={form.ampm}
                onHour={(v) => setForm({ ...form, hour: v })} onMinute={(v) => setForm({ ...form, minute: v })} onAmpm={(v) => setForm({ ...form, ampm: v })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Agreed fare (CAD)</label>
              <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-gold">$</span>
                <input data-testid="edit-amount" type="number" min="0" step="0.01" value={form.amount} onChange={set("amount")} className={`${input} pl-7`} /></div>
            </div>
            <div><label className={label}>Payment method</label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="edit-method" className="bg-navy border-gold/20 text-white h-[46px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">Card / online</SelectItem>
                  <SelectItem value="e_transfer">Interac e-Transfer</SelectItem>
                  <SelectItem value="pay_later">Pay on arrival</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Driver name</label><input data-testid="edit-driver" value={form.driver_name} onChange={set("driver_name")} placeholder="e.g. Dula" className={input} /></div>
            <div><label className={label}>Car / vehicle</label><input data-testid="edit-vehicle" value={form.vehicle} onChange={set("vehicle")} placeholder="e.g. Toyota Sienna" className={input} /></div>
          </div>
          <div><label className={label}>Status</label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="edit-status" className="bg-navy border-gold/20 text-white h-[46px]"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className={label}>Notes</label><textarea data-testid="edit-notes" rows={2} value={form.notes} onChange={set("notes")} className={`${input} resize-none`} /></div>
          <button onClick={submit} disabled={loading} data-testid="edit-save-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold py-3 font-semibold text-navy hover:bg-gold-light transition-colors duration-200 disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save & get payment link
          </button>

          {links && (
            <div className="rounded-sm border border-gold/25 bg-navy p-4 space-y-3" data-testid="edit-pay-links">
              <div className="flex items-center gap-2 text-gold text-sm font-semibold"><LinkIcon className="h-4 w-4" /> Payment link for this fare</div>
              <div className="flex gap-2">
                <input readOnly value={links.pay_url} data-testid="edit-pay-url" className={`${input} text-gold text-sm`} />
                <button onClick={() => copy(links.pay_url)} className="shrink-0 rounded-sm border border-gold/40 p-3 text-gold hover:bg-gold/10">{copied === links.pay_url ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <a href={mailto} data-testid="edit-send-email" className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 py-2.5 text-sm font-semibold text-gold hover:bg-gold/10 transition-colors duration-200"><Mail className="h-4 w-4" /> Email link</a>
                <a href={sms} data-testid="edit-send-sms" className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 py-2.5 text-sm font-semibold text-gold hover:bg-gold/10 transition-colors duration-200"><MessageSquare className="h-4 w-4" /> Text link</a>
              </div>
              <a href={links.invoice_url} target="_blank" rel="noopener noreferrer" data-testid="edit-invoice-url" className="block text-center text-xs text-slate-400 hover:text-gold">View / print invoice →</a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
